import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import { getTermsOfService } from "@/services/getTermsOfService";
import { Step } from "@WahooFitness/redesignr";
import { postEvent, useConfigContext } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

function useVideoPlayer() {
  const { locale } = useConfigContext();
  const [tosVersion, setTosVersion] = useState<string>("1");
  const [tosLocale, setTosLocale] = useState<string>("en");
  const [tosText, setTosText] = useState<string[]>([""]);

  useEffect(() => {
    let cancelled = false;
    getTermsOfService(locale).then((tos) => {
      if (cancelled) {
        return;
      }
      setTosVersion(tos.version);
      setTosLocale(tos.locale);
      setTosText(tos.paragraphs);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const { getSettingsRemoteConfigObject } = useSettingsRemoteConfig();
  const videoList = useMemo(
    () =>
      getSettingsRemoteConfigObject<{ url: string; steps: Step[] }[]>(
        "KICKR_RUN_ONBOARDING_VIDEO"
      ) || [],
    [getSettingsRemoteConfigObject]
  );

  const params = useParams();
  const videoIndex = Number(params.videoIdx ?? 0);
  const navigate = useNavigate();
  const { search } = useLocation();

  const navToFirstVideo = useCallback(() => {
    postEvent("kickrRunShippingMode", { unlock: "true", tosVersion, tosLocale });
    navigate("/onboarding/guided-video/0");
  }, [navigate, tosVersion, tosLocale]);

  const forwardNav = useCallback(() => {
    const newVideoIndex = videoIndex + 1;
    if (newVideoIndex >= videoList.length) {
      navigate(`/onboarding/onboarding-completed${search}`);
    } else {
      navigate(`/onboarding/guided-video/${newVideoIndex}${search}`);
    }
  }, [navigate, search, videoIndex, videoList.length]);

  const currentVideo = useMemo(() => videoList[videoIndex], [videoIndex, videoList]);

  return {
    navToFirstVideo,
    forwardNav,
    currentVideo,
    tosText,
    locale,
  };
}

export default useVideoPlayer;
